import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import {
  useState,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'

import { CTA } from '../../CTA'
import { P } from '../../Headings'
import {
  CurrencyType,
  EventHookInterface,
  XenditPaymentInvoiceStatus,
} from '../../../types'
import { EventsContext, UserContext } from '../../../context'
import { Hyperlink } from '../../Hyperlink'
import { SignInToJoinModal } from '../../Modals/SignInToJoinModal'
import { icons, prependHttpsToUrl, colors } from '../../../utils'
import { TimezoneUpdateModal } from '../../TimezoneUpdateModal'
import { ShareEventModal } from '../../Modals/ShareEventModal'
import { AddToCalendarModal } from '../../Modals/AddToCalendarModal'
import { PaymentSuccessModal } from '../../Modals/PaymentSuccessModal'
import { useAnalyticsEvent } from '../../../hooks/analyticsEvent'
import { WaitingListButton } from './WaitingListButton'
import { EEventTypeValue } from '../../../entities'
import { PurchaseTicketModal } from '../../Modals/PurchaseTicketModal'
import { RefundPaymentModal } from '../../Modals/RefundPaymentModal'
import { useXendit } from '../../../hooks/useXendit'

// Button.
export const ButtonContainer = styled.div`
  .buttonDropdownContainer {
    position: absolute;
    display: flex;
    flex-direction: column;
    padding: 13px 10px;
    width: 234px;
    background: #ffffff;
    box-shadow: 0px 10px 15px rgba(0, 0, 0, 0.1);
    border-radius: 6px;

    button {
      cursor: pointer;
      display: flex;

      align-items: center;
      padding: 14px;
      background: none;
      border: none;
      border-radius: 6px;
      outline: none;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: -0.5px;
      line-height: 110%;
      text-align: left;
      transition: 150ms ease-in-out background;

      &:hover {
        background-color: #f0edfd;
        svg.circle circle {
          &:not(:first-of-type) {
            stroke: ${colors.grey};
            fill: ${colors.grey};
          }
        }
      }

      svg.star {
        margin-right: 5px;
        path {
          stroke: ${colors.grey};
          fill: ${colors.grey};
        }
      }

      svg.remove {
        margin-right: 5px;
        path,
        circle {
          stroke: ${colors.grey};
        }
      }

      svg.circle {
        margin-left: auto;
        circle {
          stroke: ${colors.grey};
          &:not(:first-of-type) {
            stroke: transparent;
            fill: transparent;
          }
        }
      }
      &.buttonIsActive {
        color: ${colors.purple};
        svg.star path {
          stroke: ${colors.purple};
          fill: ${colors.purple};
        }

        svg.remove {
          path,
          circle {
            stroke: ${colors.purple};
          }
        }

        svg.circle circle {
          stroke: ${colors.purple};
          &:not(:first-of-type) {
            stroke: ${colors.purple};
            fill: ${colors.purple};
          }
        }
      }
    }
  }

  & .joinMeeting {
    & button {
      width: 238px;
    }
  }
`

export const Button = styled(CTA)`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 238px;

  .buttonIcon {
    position: relative;
    height: 24px;
    margin-right: 5px;
    width: 24px;
  }
`

const PendingContainer = styled.div`
  max-width: 100%;
  padding: 10px 16px 14px;
  background: rgba(212, 142, 238, 0.16);
  border-radius: 10px;
  color: #ca69ef;
  display: flex;
  gap: 14px;
  align-items: flex-start;

  h5 {
    font-weight: 500;
    font-size: 16px;
    line-height: 140%;
    margin: 0;
  }

  p {
    font-weight: 400;
    font-size: 14px;
    line-height: 140%;
    margin: 0;
  }
`

// Props.
interface Props {
  eventHook: EventHookInterface
  handleNotGoing: () => void
}

// Book Button.
export const BookButton: React.FC<Props> = ({ eventHook, handleNotGoing }) => {
  const { t } = useTranslation('common')

  const router = useRouter()

  const { getPaymentInvoice } = useXendit()

  const analyticEvent = useAnalyticsEvent()

  // User Context.
  const { currentUser } = useContext(UserContext)

  const eventsContext = useContext(EventsContext)
  const { fetchEvent } = eventsContext

  const { value, isInside15mins, notYetFinished, alreadyStarted } = eventHook
  const {
    host,
    id,
    event_paid_type,
    is_user_going,
    is_user_decline,
    is_full_booked,
    video_url,
    currency,
  } = value

  const [showSignInToJoinModal, setShowSignInToJoinModal] = useState(false)
  const [showButtonDropdown, setShowButtonDropdown] = useState(false)
  const [showTimezoneUpdateModal, setShowTimezoneUpdateModal] = useState(false)
  const [showShareEventModal, setShowShareEventModal] = useState(false)
  const [showEventCalendarModal, setShowEventCalendarModal] = useState(false)
  const [showPurchaseTicketModal, setShowPurchaseTicketModal] = useState(false)
  const [showRefundPaymentModal, setShowRefundPaymentModal] = useState(false)
  const [showPaymentSuccessConfirmation, setShowPaymentSuccessConfirmation] =
    useState(false)
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false)
  const [xenditPaymentStatus, setXenditPaymentStatus] = useState(null)
  const [isUserGoing, setIsUserGoing] = useState(is_user_going)
  const [isFullBooked, setIsFullBooked] = useState(is_full_booked)

  const dropdownRef = useRef<HTMLDivElement>(null)

  const checkXenditPaymentStatus = async () => {
    if (currency === CurrencyType.IDR) {
      const data = await getPaymentInvoice(id).catch(() => null)

      if (data && data.data.status) {
        setXenditPaymentStatus(data.data.status)
      }
    }
  }

  useEffect(() => {
    checkXenditPaymentStatus()
  }, [])

  useEffect(() => {
    fetchEvent(id).then((data) => {
      setIsUserGoing(data.is_user_going)

      if (data.group_size && data.participants_number > 0) {
        setIsFullBooked(
          data.participants_number < data.group_size ? false : true,
        )
      }
    })
  }, [id])

  const onClick = async () => {
    if (!currentUser.time_zone || currentUser.time_zone === '') {
      return setShowTimezoneUpdateModal(true)
    }

    setShowButtonDropdown(true)
  }

  const goingClick = async () => {
    if (!isUserGoing) {
      if (event_paid_type !== null) {
        analyticEvent.eventsRegisterForEvent(event_paid_type.toString())
        if (event_paid_type === EEventTypeValue.FREE) {
          //show join button if it's free
          await eventHook.addCurrentUserToEvent()
          setShowButtonDropdown(false)
          setShowShareEventModal(true)
          analyticEvent.fbLead()
          setIsUserGoing(true)
        }
        if (event_paid_type === EEventTypeValue.PAID) {
          //Create paymentIntent if it's paid event
          setShowButtonDropdown(false)
          setShowPurchaseTicketModal(true)
        }
      }
    } else {
      setShowButtonDropdown(false)
    }
  }

  const notGoingClick = async () => {
    if (!is_user_decline && !isUserGoing) {
      await eventHook.removeCurrentUserFromEvent()
      setShowButtonDropdown(false)
      handleNotGoing()
    } else {
      if (isUserGoing) {
        if (event_paid_type !== null) {
          if (event_paid_type === EEventTypeValue.FREE) {
            //remove user if it's free
            await eventHook.removeCurrentUserFromEvent()
            setShowButtonDropdown(false)
          }
          if (event_paid_type === EEventTypeValue.PAID) {
            //Show refund if it's paid event
            setShowButtonDropdown(false)
            setShowRefundPaymentModal(true)
          }
        }
      } else {
        setShowButtonDropdown(false)
      }
    }
    setIsUserGoing(false)
    analyticEvent.eventsDeRegisterForEvent()
  }

  const closePurchaseTicketModal = useCallback((success = false) => {
    setShowPurchaseTicketModal(false)
    if (success === true) {
      setShowPaymentSuccessModal(true)
      setShowPaymentSuccessConfirmation(true)
    }
  }, [])

  const closeRefundPaymentModal = useCallback((success = false) => {
    setShowRefundPaymentModal(false)
    if (!success) {
      console.error('Refund error')
    }
  }, [])

  const onEditClicked = () => {
    router.push(`/events/manage/${id}`)
  }

  const isCurrentUserHost = useMemo(() => {
    if (currentUser && host) {
      if (currentUser.id === host.id) {
        return true
      }
    }
    return false
  }, [currentUser, host])

  const participantProperty = useMemo(() => {
    if (
      xenditPaymentStatus === XenditPaymentInvoiceStatus.CREATED ||
      xenditPaymentStatus === XenditPaymentInvoiceStatus.PROCESSING
    ) {
      return {
        color: 'gray',
        label: t('Pending Payment', 'Pending Payment'),
      }
    }
    if (isUserGoing) {
      return {
        color: 'green',
        label: t('Going', 'Going'),
        icon: `${icons.checkCircle}`,
      }
    } else if (is_user_decline) {
      return {
        color: 'gray',
        label: t('Not going', 'Not going'),
        icon: `${icons.xCircle}`,
      }
    }
    return {
      color: 'purple',
      label: t('RSVP to event', 'RSVP to event'),
      icon: null,
    }
    // }
  }, [xenditPaymentStatus, t, isUserGoing, is_user_decline])

  const videoLink = useMemo(() => {
    return prependHttpsToUrl(video_url)
  }, [video_url])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowButtonDropdown(false)
      }
    }

    // Add Event Listener — Click.
    window && window.addEventListener('click', handleClickOutside)

    // Tear Down.
    return () =>
      window && window.removeEventListener('click', handleClickOutside)
  }, [dropdownRef])

  if (
    !eventHook.value ||
    !notYetFinished() ||
    eventHook.value.status === 1 ||
    eventHook.value.status === 2
  )
    return null

  // for non-login user
  if (!currentUser) {
    // if event is fully booked
    if (isFullBooked)
      return (
        <ButtonContainer>
          <Button className="bookButton purple" disabled>
            {t(`Event is full`, `Event is full`)}
          </Button>
        </ButtonContainer>
      )

    return (
      <>
        <ButtonContainer>
          <Button
            className="bookButton purple"
            onClick={() => {
              setShowSignInToJoinModal(true)
            }}>
            {t(`RSVP to event`, `RSVP to event`)}
          </Button>
        </ButtonContainer>
        {/* Sign In To Join Modal. */}
        {showSignInToJoinModal && (
          <SignInToJoinModal
            close={() => setShowSignInToJoinModal(false)}
            eventHook={eventHook}
          />
        )}
      </>
    )
  } else {
    if (isCurrentUserHost) {
      // if event is about to start in next 15 mins or during the event duration
      if (
        (isInside15mins() || (alreadyStarted() && notYetFinished())) &&
        isCurrentUserHost
      )
        return (
          <ButtonContainer>
            <Hyperlink className="joinMeeting" newTab={true} href={videoLink}>
              <CTA className={`bookButton green`}>
                {t(`BookButton2073917337`, `Launch your event!`)}
              </CTA>
            </Hyperlink>
          </ButtonContainer>
        )

      // return manage event button as default
      return (
        <ButtonContainer>
          <Button className="bookButton purple" onClick={onEditClicked}>
            {t(`BookButton-1526710343`, `Manage/edit your event`)}
          </Button>
        </ButtonContainer>
      )
    } else {
      // Section for non-host senario
      if (isUserGoing) {
        // if current user is participant
        if (
          (isInside15mins() || (alreadyStarted() && notYetFinished())) &&
          isUserGoing
        )
          return (
            <ButtonContainer>
              <Hyperlink className="joinMeeting" newTab={true} href={videoLink}>
                <CTA className={`bookButton green`}>
                  {t(`BookButton2113808205`, `Join event now!`)}
                </CTA>
              </Hyperlink>
            </ButtonContainer>
          )
      } else {
        // if current user hasn't joined
        // if event is fully booked
        if (isFullBooked)
          return (
            <>
              {/* If you need to debug WaitingListButton then un comment it in BookAndShare */}
              <WaitingListButton eventHook={eventHook} />
            </>
          )
      }
      return (
        <>
          {/* Button. */}
          <ButtonContainer ref={dropdownRef}>
            <Button
              className={`bookButton ${participantProperty.color}`}
              onClick={() =>
                xenditPaymentStatus !== XenditPaymentInvoiceStatus.CREATED &&
                xenditPaymentStatus !== XenditPaymentInvoiceStatus.PROCESSING &&
                onClick()
              }>
              {participantProperty.icon && (
                <div className="buttonIcon">
                  <Image
                    src={participantProperty.icon}
                    // eslint-disable-next-line i18next/no-literal-string
                    layout="fill"
                    // eslint-disable-next-line i18next/no-literal-string
                    alt="participants icon"
                  />
                </div>
              )}
              {participantProperty.label}
            </Button>

            {showButtonDropdown && (
              <div className="buttonDropdownContainer">
                <button
                  className={isUserGoing ? 'buttonIsActive' : null}
                  onClick={goingClick}>
                  <svg
                    className="star"
                    width="24"
                    height="24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12.412 4l2.6 5.266 5.812.85-4.206 4.096.993 5.788-5.199-2.734L7.213 20l.993-5.788L4 10.116l5.813-.85L12.413 4z"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{t(`Going`, `Going`)}</span>
                  <svg
                    className="circle"
                    width="16"
                    height="16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="7.5" />
                    <circle cx="8" cy="8" r="4.5" />
                  </svg>
                </button>

                <button
                  className={is_user_decline ? 'buttonIsActive' : null}
                  onClick={notGoingClick}>
                  <svg
                    className="remove"
                    width="24"
                    height="24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      strokeWidth="2"
                      strokeLinecap="round"
                      d="M7.413 7.067l8.654 9.52"
                    />
                    <circle cx="12" cy="12" r="7" strokeWidth="2" />
                  </svg>
                  <span>{t(`Not Going`, `Not Going`)}</span>
                  <svg
                    className="circle"
                    width="16"
                    height="16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="7.5" />
                    <circle cx="8" cy="8" r="4.5" />
                  </svg>
                </button>
              </div>
            )}
          </ButtonContainer>

          {/* Your payment is pending. */}
          {(xenditPaymentStatus === XenditPaymentInvoiceStatus.CREATED ||
            xenditPaymentStatus === XenditPaymentInvoiceStatus.PROCESSING) && (
            <PendingContainer>
              <img src="/icons/icon-card.svg" alt="icon card" />

              <div>
                <h5>{t('Your payment is pending.')}</h5>

                <p>{t('Please check your email to complete the payment.')}</p>
              </div>
            </PendingContainer>
          )}

          {/* update timezone modal*/}
          {showTimezoneUpdateModal && (
            <TimezoneUpdateModal
              header={t(
                `BookButton986288651`,
                `We’re missing a lil’ something about you`,
              )}
              content={t(
                `BookButton1570320882`,
                `It looks like you never set your time zone location. This helps us communicate to everyone the correct time in relation to where they are in the world.`,
              )}
              close={() => {
                setShowTimezoneUpdateModal(false)
              }}
            />
          )}
          {/* Event Calendar Modal. */}
          {showEventCalendarModal && (
            <AddToCalendarModal
              close={() => setShowEventCalendarModal(false)}
              eventHook={eventHook}
              // eslint-disable-next-line i18next/no-literal-string
              source="View Event Modal"
            />
          )}
          {/* Share Event Modal. */}
          {showShareEventModal && (
            <ShareEventModal
              header={`Success! Your attendance has been confirmed`}
              text={
                <P>
                  {t(
                    `BookButton525080713`,
                    `We'll send you a reminder email 24hrs and 15mins from the event start time. To help  remember the date we also recommend adding it to your personal calendar:`,
                  )}
                </P>
              }
              showCalendarLink={true}
              showCalendarClick={() => setShowEventCalendarModal(true)}
              close={() => setShowShareEventModal(false)}
              eventHook={eventHook}
            />
          )}
          {showPurchaseTicketModal && (
            <PurchaseTicketModal
              onClose={closePurchaseTicketModal}
              eventHook={eventHook}
            />
          )}
          {showRefundPaymentModal && (
            <RefundPaymentModal
              onClose={closeRefundPaymentModal}
              eventHook={eventHook}
              setUserStatus={setIsUserGoing}
            />
          )}
          {/* Payment Success Modal. */}
          {showPaymentSuccessModal && (
            <PaymentSuccessModal
              showCalendarClick={() => setShowEventCalendarModal(true)}
              showPaymentSuccessNotification={showPaymentSuccessConfirmation}
              closePaymentSuccessNotification={() =>
                setShowPaymentSuccessConfirmation(false)
              }
              close={() => setShowPaymentSuccessModal(false)}
            />
          )}
        </>
      )
    }
  }
}
